// src/pages/Reports/ExpirationsReport.js
import React, { useEffect, useState } from "react";
import { Container, Table, Row, Col, Button, Spinner } from "react-bootstrap";
import Select from "../../components/ReportSelect";
import axios from "axios";
import Pagination from "../../components/Pagination";
import "../../styles/Reports.css";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import ReportFilterPanel from "../../components/ReportFilterPanel";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 10;

const today = new Date();
const todayStr = today.toISOString().slice(0, 10);

const future = new Date();
future.setDate(today.getDate() + 30);
const futureStr = future.toISOString().slice(0, 10);

export default function ExpirationsReport() {
  const navigate = useNavigate();

  // dropdowns
  const [dropdowns, setDropdowns] = useState({
    orgUnits: [],
    categories: [],
    subCategories: [],
    groups: [],
    statuses: [],
    docTypes: [],
  });

  // pending (UI controls)
  const [pStartDate, setPStartDate] = useState(todayStr);
  const [pEndDate, setPEndDate] = useState(futureStr);
  const [pDocTypes, setPDocTypes] = useState([]); // [{label, value}]
  const [pOrgUnits, setPOrgUnits] = useState([]);
  const [pCategory, setPCategory] = useState([]); // [{label, value}]
  const [pSubCategory, setPSubCategory] = useState([]);
  const [pGroups, setPGroups] = useState([]);
  const [pStatus, setPStatus] = useState([]); // [{label, value}]
  const [pQuery, setPQuery] = useState(""); // (optional) Search, shown on second row

  // data/runtime
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // fetch dropdowns (Applicant Type, Status, Doc Types)
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const [org, cat, subCat, groups, stat, docs] = await Promise.all([
          axios.get(`${BASE_URL}/api/dropdowns/org-units`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/sub-categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/position-groups`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/user-status`, { headers }),
          axios.get(`${BASE_URL}/api/reports/expirations/doc-types`, {
            headers,
          }),
        ]);

        setDropdowns({
          orgUnits: Array.isArray(org.data) ? org.data : [],
          categories: Array.isArray(cat.data) ? cat.data : [],
          subCategories: Array.isArray(subCat.data) ? subCat.data : [],
          groups: Array.isArray(groups.data) ? groups.data : [],
          statuses: Array.isArray(stat.data) ? stat.data : [],
          docTypes: Array.isArray(docs.data)
            ? docs.data.map((d) => ({ label: d, value: d }))
            : [],
        });
      } catch (err) {
        console.error("Dropdown fetch failed", err);
        setDropdowns({
          orgUnits: [],
          categories: [],
          subCategories: [],
          groups: [],
          statuses: [],
          docTypes: [],
        });
      }
    };
    fetchDropdowns();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setHasSearched(true);
    setCurrentPage(1);

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const params = {
        startDate: pStartDate || undefined,
        endDate: pEndDate || undefined,
        docTypes: pDocTypes.map((d) => d.value).join(",") || undefined,
        orgUnit: pOrgUnits.map((o) => o.value).join(",") || undefined,
        category: pCategory.map((c) => c.value).join(",") || undefined,
        subCategory:
          pSubCategory.map((s) => s.value).join(",") || undefined,
        group: pGroups.map((g) => g.value).join(",") || undefined,
        status: pStatus.map((s) => s.value).join(",") || undefined,
        search: (pQuery || "").trim() || undefined,
      };

      const res = await axios.get(`${BASE_URL}/api/reports/expirations`, {
        headers,
        params,
      });

      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Expirations fetch error:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setPStartDate(todayStr);
    setPEndDate(futureStr);
    setPDocTypes([]);
    setPOrgUnits([]);
    setPCategory([]);
    setPSubCategory([]);
    setPGroups([]);
    setPStatus([]);
    setPQuery("");
    setData([]);
    setHasSearched(false);
    setCurrentPage(1);
  };

  const paged = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const exportCSV = () => {
    if (data.length === 0) {
      alert("No data to export");
      return;
    }
    const headers = [
      "Org Unit",
      "Last Name",
      "First Name",
      "Document Type",
      "Document Number",
      "Expiry Date",
      "Days To Expiration",
      "Employee ID",
    ];
    const rows = data.map((r) =>
      [
        r.org_unit ?? "",
        r.last_name ?? "",
        r.first_name ?? "",
        r.document_type ?? "",
        r.document_number ?? "",
        r.expiry_date ? String(r.expiry_date).substring(0, 10) : "",
        r.days_to_expiration ?? "",
        r.userid_no ?? "",
      ]
        .map((c) => `"${String(c).replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    saveAs(blob, "ExpirationsReport.csv");
  };

  return (
    <Container className="form-wrapper my-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
        <h3 className="report-page-title fw-bold m-0"><span className="report-title-icon" aria-hidden="true"><i className="fas fa-calendar-times" /></span><span>Expirations</span></h3>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          ⬅ Return to Report List
        </Button>
      </div>

      {/* Filter row — matches your Time & Attendance pattern */}
      <ReportFilterPanel>
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={2}>
          <label>Start Date</label>
          <input
            type="date"
            className="form-control"
            placeholder="yyyy-mm-dd"
            value={pStartDate}
            onChange={(e) => setPStartDate(e.target.value)}
          />
        </Col>

        <Col md={2}>
          <label>End Date</label>
          <input
            type="date"
            className="form-control"
            placeholder="yyyy-mm-dd"
            value={pEndDate}
            onChange={(e) => setPEndDate(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <label>Document Types</label>
          <Select
            isMulti
            options={dropdowns.docTypes}
            value={pDocTypes}
            onChange={setPDocTypes}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>

        <Col md={2}>
          <label>Status</label>
          <Select
            isMulti
            options={(dropdowns.statuses || []).map((s) => ({
              label: s.s_TranslatedText,
              value: s.s_id,
            }))}
            value={pStatus}
            onChange={setPStatus}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
      </Row>
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={3}>
          <label>Org Units</label>
          <Select
            isMulti
            options={(dropdowns.orgUnits || []).map((o) => ({
              label: o.ou_name,
              value: o.ou_id,
            }))}
            value={pOrgUnits}
            onChange={setPOrgUnits}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
        <Col md={3}>
          <label>Employment Categories</label>
          <Select
            isMulti
            options={(dropdowns.categories || []).map((c) => ({
              label: c.c_name,
              value: c.c_id,
            }))}
            value={pCategory}
            onChange={setPCategory}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
        <Col md={3}>
          <label>Employment Sub-Category</label>
          <Select
            isMulti
            options={(dropdowns.subCategories || []).map((s) => ({
              label: s.sc_name,
              value: s.sc_id,
            }))}
            value={pSubCategory}
            onChange={setPSubCategory}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
        <Col md={3}>
          <label>Groups</label>
          <Select
            isMulti
            options={(dropdowns.groups || []).map((g) => ({
              label: g.pg_name,
              value: g.pg_id,
            }))}
            value={pGroups}
            onChange={setPGroups}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
      </Row>
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={3}>
          <label>Search</label>
          <input
            type="text"
            className="form-control"
            placeholder="ID / Name"
            value={pQuery}
            onChange={(e) => setPQuery(e.target.value)}
          />
        </Col>
      </Row>
      </ReportFilterPanel>
      {/* Filter + Clear */}
      <Row className="mb-3 g-3 align-items-end report-row report-actions-row">
        <Col md="auto" className="align-end">
          <div className="button-group tight">
            <Button variant="primary" onClick={fetchData}>
              <i className="fas fa-filter" aria-hidden="true" /> Filter
            </Button>
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filter
            </Button>
          </div>
        </Col>
        {/* Export pinned to the far right */}
        <Col md="auto" className="align-end ms-auto">
          <Button
            className="btn-export"
            onClick={exportCSV}
            disabled={!hasSearched || data.length === 0}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      {!hasSearched ? (
        <div className="text-center text-muted py-5">
          Use the filters above and click <strong>Filter</strong> to load
          results.
        </div>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="table-responsive">
          <Table bordered hover size="sm" className="compact-table">
            <thead className="table-dark text-center">
              <tr>
                <th>ORG UNIT</th>
                <th>LAST NAME</th>
                <th>FIRST NAME</th>
                <th>DOCUMENT TYPE</th>
                <th>EXPIRY DATE</th>
                <th>DAYS TO EXPIRATION</th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No results found with current filters.
                  </td>
                </tr>
              ) : (
                paged.map((r, i) => (
                  <tr key={`${r.user_id}-${r.document_type}-${i}`}>
                    <td>{r.org_unit || ""}</td>
                    <td>{r.last_name || ""}</td>
                    <td>{r.first_name || ""}</td>
                    <td>{r.document_type}</td>
                    <td>
                      {r.expiry_date
                        ? String(r.expiry_date).substring(0, 10)
                        : ""}
                    </td>
                    <td>{r.days_to_expiration}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Pagination
            currentPage={currentPage}
            totalItems={data.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </Container>
  );
}
